import React, { useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  ScrollView,
} from 'react-native';
import { useResponsive } from '@/hooks/common/useResponsive';
import { IDataTableProps, IColumnConfig } from '@/types/admin/dataTable';
import { DESKTOP_CONSTANTS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { DesktopTableFilters } from './DesktopTableFilters';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { DesktopTableHeader } from './DesktopTableHeader';
import { DesktopPagination } from './DesktopPagination';
import { useDesktopDataTableStyles } from './DesktopDataTableStyles';
import { EMPTY_MESSAGES } from '@/constants/admin/emptyMessages';


export const DesktopDataTable = <T,>({
  data,
  columns,
  isLoading,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  showSearch = true,
  showSort = true,
  showFilter = true,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  paginationModel,
  onPaginationChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onRowSelect,
  selectedRows = [],
  onRowSelectionChange,
  enableRowSelection = false,
  onSelectAll,
  onDeselectAll,
  updateCsvData,
  emptyStateType = 'default',
  containerStyle,
}: IDataTableProps<T>) => {
  const { columnsToShow } = useResponsive();
  const styles = useDesktopDataTableStyles();
  const { admin } = useAdminColors();
  const showFiltersHeader = showSearch || showSort || showFilter;

  // Filter columns based on screen size and visibility settings
  const visibleColumns = useMemo(() => {
    return columns.filter(column => {
      if (!column.visible) return false;

      switch (columnsToShow) {
        case 'minimal':
          return column.mobileVisible !== false;
        case 'partial':
        case 'full':
          return true;
        default:
          return column.visible;
      }
    });
  }, [columns, columnsToShow]);

  // Calculate column widths
  const getColumnWidth = (column: IColumnConfig<T>, _index: number): number => {
    if (column.width && typeof column.width === 'number') return column.width;

    // Give more space to text columns, less to status/boolean columns
    if (column.sortType === 'boolean') return 100;
    if (column.key === 'name') return 200;

    return Math.max(DESKTOP_CONSTANTS.TABLE_MIN_COLUMN_WIDTH, 150);
  };

  const baseColumnWidths = useMemo(() => {
    return visibleColumns.map((col, idx) => getColumnWidth(col, idx));
  }, [visibleColumns]);

  const columnFlexGrow = useMemo(() => {
    const sumBase = baseColumnWidths.reduce((a, b) => a + b, 0) || 1;
    return baseColumnWidths.map(w => w / sumBase);
  }, [baseColumnWidths]);

  // Ensure non-optional values for filter/sort props
  const effectiveFilterModel = useMemo(() => (
    filterModel ?? { items: [] }
  ), [filterModel]);
  const effectiveOnFilterChange = useMemo(() => (
    onFilterChange ?? (() => {})
  ), [onFilterChange]);
  const effectiveSortModel = useMemo(() => (
    sortModel ?? []
  ), [sortModel]);
  const effectiveOnSortChange = useMemo(() => (
    onSortChange ?? (() => {})
  ), [onSortChange]);

  const renderTableHeader = () => (
    <DesktopTableHeader
      columns={visibleColumns}
      columnFlexGrow={columnFlexGrow}
      sortModel={effectiveSortModel}
      onSortChange={effectiveOnSortChange}
      enableRowSelection={enableRowSelection}
      selectedCount={selectedRows.length}
      totalCount={data.rows.length}
      onSelectAll={onSelectAll}
      onDeselectAll={onDeselectAll}
    />
  );

  const renderTableRow = useCallback((item: T, rowIndex: number) => {
    const isSelected = enableRowSelection && selectedRows.some(selectedRow =>
      (selectedRow as any).id === (item as any).id
    );

    const rowId = (item as any)?.id ?? rowIndex;
    const isAlt = rowIndex % 2 === 1;

    return (
      <TouchableOpacity
        key={rowId}
        style={[
          styles.tableRow,
          isAlt && styles.tableRowAlt,
          isSelected && styles.selectedRow,
        ]}
        onPress={() => onRowSelect?.(item, rowIndex)}
        activeOpacity={0.7}
      >
          <View style={styles.rowContent}>
            {enableRowSelection && (
              <View style={[styles.cell, { width: 50 }]}>
                <TouchableOpacity
                  onPress={() => {
                    const { toggleSelection } = require('@/utils/admin/selection');
                    onRowSelectionChange?.(toggleSelection(selectedRows as any[], item as any));
                  }}
                  style={styles.checkbox}
                >
                  {isSelected && (
                    <SVG_COMPONENTS.CHECK_CIRCLE
                      width={16}
                      height={16}
                      color={admin.formInput.borderFocused}
                    />
                  )}
                </TouchableOpacity>
              </View>
            )}

            {visibleColumns.map((column, cellIndex) => {
              const value = (item as any)[column.key];
              const textValue = value == null ? '—' : String(value);

              return (
                <View
                  key={column.key as string}
                  style={[
                    styles.cell,
                    { flexGrow: columnFlexGrow[cellIndex], flexBasis: 0 }
                  ]}
                >
                  {column.renderCell ? (
                    column.renderCell(value, item, rowIndex)
                  ) : (
                    Array.isArray(value) ? (
                      <View style={styles.arrayCellContainer}>
                        <ScrollView style={styles.arrayCellScroll} showsVerticalScrollIndicator={true}>
                          {value.map((v: any, idx: number) => (
                            <DesktopOverflowText key={`cell-${String(column.key)}-${idx}`} text={String(v ?? '')} numberOfLines={1} />
                          ))}
                        </ScrollView>
                      </View>
                    ) : (
                      <DesktopOverflowText text={textValue} numberOfLines={2} />
                    )
                  )}
                </View>
              );
            })}
          </View>
      </TouchableOpacity>
    );
  }, [enableRowSelection, selectedRows, onRowSelectionChange, visibleColumns, columnFlexGrow, onRowSelect, admin.formInput.borderFocused, styles]);

  const renderItem: ListRenderItem<T> = useCallback(({ item, index }) => renderTableRow(item, index), [renderTableRow]);
  const keyExtractor = useCallback((item: any, index: number) => String(item?.id ?? index), []);

  // Ensure non-optional values for pagination props
  const effectivePaginationModel = useMemo(() => (
    paginationModel ?? { page: 0, pageSize: 25 }
  ), [paginationModel]);
  const effectiveHasNextPage = useMemo(() => (
    hasNextPage ?? ((effectivePaginationModel.page + 1) * effectivePaginationModel.pageSize < data.count)
  ), [hasNextPage, effectivePaginationModel, data.count]);
  const effectiveHasPreviousPage = useMemo(() => (
    hasPreviousPage ?? (effectivePaginationModel.page > 0)
  ), [hasPreviousPage, effectivePaginationModel]);
  const effectiveOnNextPage = useMemo(() => (
    onNextPage ?? (() => {
      if (effectiveHasNextPage) {
        onPaginationChange?.({ ...effectivePaginationModel, page: effectivePaginationModel.page + 1 });
      }
    })
  ), [onNextPage, effectiveHasNextPage, onPaginationChange, effectivePaginationModel]);
  const effectiveOnPreviousPage = useMemo(() => (
    onPreviousPage ?? (() => {
      if (effectiveHasPreviousPage) {
        onPaginationChange?.({ ...effectivePaginationModel, page: effectivePaginationModel.page - 1 });
      }
    })
  ), [onPreviousPage, effectiveHasPreviousPage, onPaginationChange, effectivePaginationModel]);

  const renderPagination = () => (
    <DesktopPagination
      dataCount={data.count}
      paginationModel={effectivePaginationModel}
      onPaginationChange={onPaginationChange}
      hasNextPage={effectiveHasNextPage}
      hasPreviousPage={effectiveHasPreviousPage}
      onNextPage={effectiveOnNextPage}
      onPreviousPage={effectiveOnPreviousPage}
    />
  );

  const renderEmptyState = () => {
    const section = (EMPTY_MESSAGES as any)[emptyStateType] || EMPTY_MESSAGES.default;
    const cfg = searchQuery && (section as any).noResults
      ? (section as any).noResults(searchQuery)
      : ((section as any).default || EMPTY_MESSAGES.default);
    return (
      <View style={styles.emptyStateContainer}>
        <Text style={styles.emptyStateTitle}>
          {cfg.title}
        </Text>
        {!!cfg.message && (
          <Text style={styles.emptyStateMessage}>
            {cfg.message}
          </Text>
        )}
      </View>
    );
  };

  if (isLoading && data.rows.length === 0) {
    return (
      <GlassCard padding={0} style={containerStyle}>
        <View style={styles.container}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={admin.formInput.borderFocused} />
            <Text style={styles.loadingText}>
              Loading...
            </Text>
          </View>
        </View>
      </GlassCard>
    );
  }

  return (
    <GlassCard padding={0} style={containerStyle}>
      <View style={styles.container}>
        {showFiltersHeader && (
          <DesktopTableFilters
            columns={columns}
            searchQuery={searchQuery || ''}
            onSearchChange={onSearchChange}
            searchPlaceholder={searchPlaceholder}
            showSearch={showSearch}
            showSort={showSort}
            showFilter={showFilter}
            filterModel={effectiveFilterModel}
            onFilterChange={effectiveOnFilterChange}
            sortModel={effectiveSortModel}
            onSortChange={effectiveOnSortChange}
            showExport={false}
            onExport={() => {
              if (updateCsvData) {
                updateCsvData(data.rows);
              }
            }}/>
        )}

        {renderTableHeader()}

        {data.rows.length === 0 ? (
          renderEmptyState()
        ) : (
          <FlatList
            data={data.rows as unknown as T[]}
            renderItem={renderItem}
            keyExtractor={keyExtractor as any}
            style={styles.tableBody}
            showsVerticalScrollIndicator={true}
            removeClippedSubviews
            windowSize={10}
            initialNumToRender={25}
            maxToRenderPerBatch={25}
            updateCellsBatchingPeriod={50}
          />
        )}

        {data.rows.length > 0 && renderPagination()}

        {isLoading && data.rows.length > 0 && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="small" color={admin.formInput.borderFocused} />
          </View>
        )}
      </View>
    </GlassCard>
  );
};
